import { useQuery } from '@apollo/client';
import React, { useEffect, useMemo, useState } from 'react';

import {
  Combobox,
  ComboboxDropdown,
  ComboboxEmpty,
  ComboboxOption,
  ComboboxSelectButton,
  Stack,
  Text,
} from '@eluve/components';
import { DbEnums, LlmOutputTypesLookup } from '@eluve/graphql-types';

import { getPromptTemplatesForOverrideQuery } from '../operations';

import { OverrideSection } from './OverrideSection';
import { VariantConfig } from './types';

const ALLOWED_OUTPUT_TYPES = [
  DbEnums.LlmOutputType.SOAP_NOTE,
  DbEnums.LlmOutputType.DYNAMIC_OUTPUT,
] as const;

const OUTPUT_TYPE_OPTIONS = Object.values(LlmOutputTypesLookup).filter(
  (type): type is (typeof ALLOWED_OUTPUT_TYPES)[number] =>
    ALLOWED_OUTPUT_TYPES.includes(
      type as (typeof ALLOWED_OUTPUT_TYPES)[number],
    ),
);

interface PromptTemplateOverrideProps {
  config: VariantConfig;
  onChange: (updates: Partial<VariantConfig>) => void;
  onOutputTypeChange?: (outputType: string | undefined) => void;
}

export const PromptTemplateOverride: React.FC<PromptTemplateOverrideProps> = ({
  config,
  onChange,
  onOutputTypeChange,
}) => {
  const { data } = useQuery(getPromptTemplatesForOverrideQuery);

  const hasValues =
    config.PROMPT_TEMPLATE_ID !== undefined ||
    config.PROMPT_TEMPLATE_VARIANT_ID !== undefined;

  const [isExpanded, setIsExpanded] = useState(hasValues);
  const [selectedOutputType, setSelectedOutputType] = useState<string | null>(
    null,
  );

  const allTemplates = useMemo(() => {
    return (
      data?.promptTemplates.filter((p) =>
        ALLOWED_OUTPUT_TYPES.includes(
          p.outputType as (typeof ALLOWED_OUTPUT_TYPES)[number],
        ),
      ) ?? []
    );
  }, [data]);

  // Find the selected template from all templates (not filtered by output type)
  // This ensures we can derive the output type from existing config
  const selectedTemplate = useMemo(() => {
    return allTemplates.find((t) => t.id === config.PROMPT_TEMPLATE_ID);
  }, [allTemplates, config.PROMPT_TEMPLATE_ID]);

  // Initialize selectedOutputType from the selected template when data loads
  useEffect(() => {
    if (selectedTemplate?.outputType && selectedOutputType === null) {
      setSelectedOutputType(selectedTemplate.outputType);
    }
  }, [selectedTemplate?.outputType, selectedOutputType]);

  const templates = useMemo(() => {
    if (!selectedOutputType) {
      return allTemplates;
    }
    return allTemplates.filter((p) => p.outputType === selectedOutputType);
  }, [allTemplates, selectedOutputType]);

  const variants = useMemo(() => {
    return selectedTemplate?.prompt_template_variants ?? [];
  }, [selectedTemplate]);

  useEffect(() => {
    if (config.PROMPT_TEMPLATE_ID && !config.PROMPT_TEMPLATE_VARIANT_ID) {
      const defaultVariant = variants.find((v) => v.isCurrentDefault);
      if (defaultVariant) {
        onChange({
          PROMPT_TEMPLATE_VARIANT_ID: `${defaultVariant.promptTemplateId}:${defaultVariant.variantNumber}`,
        });
      }
    }
  }, [
    config.PROMPT_TEMPLATE_ID,
    config.PROMPT_TEMPLATE_VARIANT_ID,
    variants,
    onChange,
  ]);

  useEffect(() => {
    onOutputTypeChange?.(selectedTemplate?.outputType ?? undefined);
  }, [selectedTemplate?.outputType, onOutputTypeChange]);

  const handleEnabledChange = (enabled: boolean) => {
    setIsExpanded(enabled);
    if (!enabled) {
      setSelectedOutputType(null);
      onChange({
        PROMPT_TEMPLATE_ID: undefined,
        PROMPT_TEMPLATE_VARIANT_ID: undefined,
      });
      onOutputTypeChange?.(undefined);
    }
  };

  const handleOutputTypeChange = (outputType: string) => {
    setSelectedOutputType(outputType);
    onChange({
      PROMPT_TEMPLATE_ID: undefined,
      PROMPT_TEMPLATE_VARIANT_ID: undefined,
    });
  };

  const handleTemplateChange = (templateId: string) => {
    onChange({
      PROMPT_TEMPLATE_ID: templateId,
      PROMPT_TEMPLATE_VARIANT_ID: undefined,
    });
  };

  const selectedVariantKey = config.PROMPT_TEMPLATE_VARIANT_ID;
  const selectedVariant = variants.find(
    (v) => `${v.promptTemplateId}:${v.variantNumber}` === selectedVariantKey,
  );

  return (
    <OverrideSection
      label="Override Prompt Template"
      enabled={isExpanded}
      onEnabledChange={handleEnabledChange}
    >
      <Stack gap={3}>
        <Stack gap={1}>
          <Text size="xs" color="secondary">
            Output Type
          </Text>
          <Combobox>
            <ComboboxSelectButton className="w-full">
              {selectedOutputType ?? 'Select output type'}
            </ComboboxSelectButton>
            <ComboboxDropdown searchPlaceholder="Search output types...">
              <ComboboxEmpty>No output types found</ComboboxEmpty>
              {OUTPUT_TYPE_OPTIONS.map((type) => (
                <ComboboxOption
                  key={type}
                  onSelect={() => handleOutputTypeChange(type)}
                  searchValue={type}
                >
                  {type}
                </ComboboxOption>
              ))}
            </ComboboxDropdown>
          </Combobox>
        </Stack>

        <Stack gap={1}>
          <Text size="xs" color="secondary">
            Template
          </Text>
          <Combobox>
            <ComboboxSelectButton className="w-full">
              {selectedTemplate
                ? `${selectedTemplate.name}${selectedTemplate.outputType ? ` (${selectedTemplate.outputType})` : ''}`
                : 'Select template'}
            </ComboboxSelectButton>
            <ComboboxDropdown searchPlaceholder="Search templates...">
              <ComboboxEmpty>No templates found</ComboboxEmpty>
              {templates.map((template) => (
                <ComboboxOption
                  key={template.id}
                  onSelect={() => handleTemplateChange(template.id)}
                  searchValue={`${template.name} ${template.outputType ?? ''} ${template.id}`}
                >
                  <Stack justify="between" gap={2} direction="horizontal">
                    <Text size="md" inline>
                      {template.name}
                      {template.outputType && (
                        <Text
                          inline
                          size="xs"
                          color="tertiary"
                          className="ml-1"
                        >
                          ({template.outputType})
                        </Text>
                      )}
                    </Text>
                    <Text
                      inline
                      size="xs"
                      className="font-mono"
                      color="tertiary"
                    >
                      {template.id.slice(0, 8)}
                    </Text>
                  </Stack>
                </ComboboxOption>
              ))}
            </ComboboxDropdown>
          </Combobox>
        </Stack>

        {config.PROMPT_TEMPLATE_ID && variants.length > 0 && (
          <Stack gap={1}>
            <Text size="xs" color="secondary">
              Variant
            </Text>
            <Combobox>
              <ComboboxSelectButton className="w-full">
                {selectedVariant
                  ? `v${selectedVariant.variantNumber}${selectedVariant.isCurrentDefault ? ' (default)' : ''}`
                  : 'Select variant'}
              </ComboboxSelectButton>
              <ComboboxDropdown searchPlaceholder="Search variants...">
                <ComboboxEmpty>No variants found</ComboboxEmpty>
                {variants.map((variant) => {
                  const key = `${variant.promptTemplateId}:${variant.variantNumber}`;
                  return (
                    <ComboboxOption
                      key={key}
                      onSelect={() =>
                        onChange({ PROMPT_TEMPLATE_VARIANT_ID: key })
                      }
                      searchValue={`v${variant.variantNumber}`}
                    >
                      v{variant.variantNumber}
                      {variant.isCurrentDefault ? ' (default)' : ''}
                    </ComboboxOption>
                  );
                })}
              </ComboboxDropdown>
            </Combobox>
          </Stack>
        )}
      </Stack>
    </OverrideSection>
  );
};
